"use client"

import React, { createContext, useContext, useEffect, useRef } from "react"
import posthog, { PostHog } from "posthog-js"

import { posthogApiKey, posthogApiHost, appEnv } from "../../constants"

interface PostHogContextType {
  posthog: PostHog | null
  capture: (eventName: string, properties?: Record<string, unknown>) => void
}

const PostHogContext = createContext<PostHogContextType>({
  posthog: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  capture: () => {},
})

/**
 * Hook to access PostHog instance and capture events.
 *
 * Usage:
 *   const { capture } = usePostHog()
 *   capture("event_finder_link_clicked", { url: "...", source: "event_finder" })
 */
export function usePostHog() {
  return useContext(PostHogContext)
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) return
    if (!posthogApiKey) {
      if (appEnv === "development") {
        console.warn(
          "[PostHog] NEXT_PUBLIC_POSTHOG_KEY not set. PostHog tracking is disabled.",
        )
      }
      return
    }

    posthog.init(posthogApiKey, {
      api_host: posthogApiHost,
      // Capture page views automatically on route change
      capture_pageview: true,
      // Capture page leaves for session analysis
      capture_pageleave: true,
      // Respect Do Not Track browser setting
      respect_dnt: true,
      // Disable in development unless key is explicitly set
      loaded: (ph) => {
        if (appEnv === "development") {
          // Optionally enable debug mode in development
          ph.debug()
        }
      },
    })

    initialized.current = true
  }, [])

  const capture = (
    eventName: string,
    properties?: Record<string, unknown>,
  ) => {
    if (!posthogApiKey) return
    posthog.capture(eventName, properties)
  }

  return (
    <PostHogContext.Provider value={{ posthog, capture }}>
      {children}
    </PostHogContext.Provider>
  )
}
